import { useState } from "react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HelpCircle, Lightbulb, X, ArrowRight } from "lucide-react";

// Inline "?" help next to a field or section.
export function HelpHint({ text, testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Help" data-testid={testid || "help-hint"}
          className="inline-grid place-items-center h-4 w-4 rounded-full text-slate-400 hover:text-[#2EA3FD] align-middle">
          <HelpCircle size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-64 text-sm text-slate-600 leading-relaxed">{text}</PopoverContent>
    </Popover>
  );
}

// Small "?" that deep-links to a specific Help centre article for the current page.
export function HelpLink({ article, label = "Help", tone = "light", testid }) {
  const onDark = tone === "dark";
  return (
    <Link to={`/help?article=${article}`} data-testid={testid || `help-link-${article}`}
      title="Open help for this page"
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${onDark ? "bg-white/15 text-white hover:bg-white/25" : "border border-slate-200 bg-white text-slate-500 hover:text-[#2EA3FD] hover:border-[#2EA3FD]"}`}>
      <HelpCircle size={14} />{label}
    </Link>
  );
}

// One-time, dismissable coaching bubble at the top of a page.
export function CoachTip({ tipKey, title, children }) {
  const storeKey = `cass_tip_${tipKey}`;
  const [show, setShow] = useState(() => localStorage.getItem(storeKey) !== "1");
  if (!show) return null;
  const dismiss = () => { localStorage.setItem(storeKey, "1"); setShow(false); };
  return (
    <div className="relative rounded-xl border border-sky-200 bg-gradient-to-r from-[#EFF8FF] to-white px-4 py-3 flex items-start gap-3" data-testid={`coach-tip-${tipKey}`}>
      <span className="rounded-lg bg-[#2EA3FD]/10 text-[#2EA3FD] p-1.5 shrink-0"><Lightbulb size={16} /></span>
      <div className="min-w-0 flex-1 pr-6">
        {title && <p className="text-sm font-semibold text-[#0B1A2F]">{title}</p>}
        <p className="text-sm text-slate-600 mt-0.5">{children}</p>
        <Link to="/help" className="inline-flex items-center gap-1 text-xs font-medium text-[#2EA3FD] mt-1.5 hover:underline">Visit the Help centre<ArrowRight size={12} /></Link>
      </div>
      <button onClick={dismiss} aria-label="Dismiss tip" data-testid={`coach-dismiss-${tipKey}`}
        className="absolute top-2.5 right-2.5 text-slate-400 hover:text-slate-600"><X size={15} /></button>
    </div>
  );
}
